import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

// Redux actions
import { fetchInvitations } from '../../store/slices/invitationSlice';
import { openModal } from '../../store/slices/uiSlice';

// Components
import { InvitationList, CreateInvitationModal, InvitationDetailModal } from '../../components/admin/invitations';
import { LoadingSpinner, ErrorMessage } from '../../components/common';
import AdminLayout from '../../components/admin/AdminLayout';

// Stats card component
const StatCard = ({ title, value, icon, color }) => {
  // We don't need theme here since we're using the color prop directly
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 56,
                width: 56,
                borderRadius: '50%',
                backgroundColor: `${color}.light`,
                color: `${color}.main`
              }}
            >
              {icon}
            </Box>
          </Grid>
          <Grid item xs>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4">
              {value}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const InvitationsPage = () => {
  const dispatch = useDispatch();
  const { items, loading, error, filters, stats } = useSelector(state => state.invitations);
  
  // Create a stable string representation of filters for the dependency array
  const filtersString = useMemo(() => JSON.stringify(filters), [filters]);
  
  // Create a debounced fetch function that won't trigger too frequently
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((currentFilters) => {
      // Use a stable reference to the filters object to prevent re-fetching when no actual changes
      const stableFilters = { 
        status: currentFilters.status || 'all',
        page: currentFilters.page || 1,
        limit: currentFilters.limit || 10,
        _refresh: currentFilters._refresh // Allow refresh timestamp to come through
      };
      console.log('InvitationsPage fetching with filters:', stableFilters);
      dispatch(fetchInvitations(stableFilters));
    }, 300),
    [dispatch]
  );
  
  // Load invitations on component mount or when filters actually change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    debouncedFetch(filters);
    
    // Cleanup on unmount
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch, filtersString]); // Using the memoized string representation
  
  // Handle refresh with immediate fetch (bypass debounce)
  const handleRefresh = () => {
    console.log('Manual refresh triggered with filters:', filters);
    // Cancel any pending debounced fetches
    debouncedFetch.cancel();
    
    // Add a timestamp to force a fresh fetch while keeping other filter parameters
    const refreshedFilters = { 
      ...filters, 
      _refresh: Date.now() 
    };
    
    // Dispatch immediate fetch with the refreshed filters
    dispatch(fetchInvitations(refreshedFilters));
  };
  
  // Handle create invitation
  const handleCreateInvitation = () => {
    dispatch(openModal('createInvitation'));
  };
  
  return (
    <AdminLayout title="Astrologer Invitations">
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Astrologer Invitations
        </Typography>
        
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateInvitation}
          >
            Create Invitation
          </Button>
        </Box>
      </Box>
      
      {/* Stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Invitations"
            value={stats.total}
            icon={<PersonIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={<RefreshIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Used"
            value={stats.used}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expired"
            value={stats.expired}
            icon={<ErrorIcon />}
            color="error"
          />
        </Grid>
      </Grid>
      
      {/* Main content */}
      <Paper sx={{ p: 3 }}>
        {error && <ErrorMessage message={error} sx={{ mb: 3 }} />}
        
        {loading && items.length === 0 ? (
          <LoadingSpinner />
        ) : (
          <InvitationList
            invitations={items}
            loading={loading}
          />
        )}
      </Paper>
      
      {/* Modals */}
      <CreateInvitationModal />
      <InvitationDetailModal />
    </Container>
    </AdminLayout>
  );
};

export default InvitationsPage;
